import React, { PureComponent } from 'react';
import styled, { keyframes } from 'styled-components';

import EventEmitter from '../../services/event-emitter';
import { submitSupportMessage } from '../../api/ContactSupport';
import mgLogo from '../../containers/assets/New MG Logo (Icon).png';

// Event fired by any entry point (FAB, user-menu item) to open the
// support modal. Handled by ContactSupport below.
export const OPEN_CONTACT_SUPPORT_EVENT = 'open-contact-support';

const SUBJECT_MAX = 200;
const MESSAGE_MAX = 500;
const MESSAGE_COUNTER_WARN = 475;

const FONT = "'Mukta Vaani', sans-serif";

// Bottom-right floating help button. z-index chosen to sit above
// the leftbar (default z-index) but below the modal itself so that
// opening the modal covers the button with the backdrop.
const Fab = styled.button`
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: #52C5CD;
  color: #ffffff;
  font-family: ${FONT};
  font-weight: 700;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  z-index: 1400;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 120ms ease, box-shadow 120ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.25);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(82, 197, 205, 0.35), 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 27, 59, 0.55);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 560px;
  padding: 32px 36px;
  font-family: ${FONT};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const LogoIcon = styled.img`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 22px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-family: ${FONT};
  font-size: 24px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.55);
  cursor: pointer;
  padding: 0 4px;

  &:hover { color: rgba(0, 27, 59, 0.85); }
`;

const Subtitle = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.72);
  margin: 0 0 8px;
`;

const IntroNote = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.55);
  margin: 0 0 20px;
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.94);
  margin-bottom: 6px;
`;

const inputStyles = `
  width: 100%;
  border: 1px solid rgba(0, 27, 59, 0.2);
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.94);
  font-family: 'Mukta Vaani', sans-serif;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #52C5CD;
    box-shadow: 0 0 0 3px rgba(82, 197, 205, 0.18);
  }

  &:disabled {
    background: #f6f7f9;
    color: rgba(0, 27, 59, 0.55);
    cursor: not-allowed;
  }
`;

const Input = styled.input`${inputStyles}`;

const Textarea = styled.textarea`
  ${inputStyles}
  resize: vertical;
  min-height: 140px;
`;

const CounterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 4px;
`;

const Counter = styled.div`
  font-size: 16px;
  line-height: 20px;
  color: ${p => (p.warn ? '#c74234' : 'rgba(0, 27, 59, 0.55)')};
`;

const FieldError = styled.div`
  margin-top: 6px;
  font-size: 16px;
  line-height: 20px;
  color: #c74234;
`;

const FormError = styled.div`
  margin: 8px 0 16px;
  padding: 10px 12px;
  background: #fdecea;
  border: 1px solid rgba(199, 66, 52, 0.35);
  border-radius: 6px;
  font-size: 16px;
  line-height: 22px;
  color: #7a2a20;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
`;

const CancelButton = styled.button`
  background: transparent;
  border: 1.5px solid rgba(0, 27, 59, 0.2);
  border-radius: 4px;
  padding: 8px 20px;
  font-family: ${FONT};
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.85);
  cursor: pointer;

  &:hover { background: rgba(0, 27, 59, 0.04); }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const SubmitButton = styled.button`
  background: #52C5CD;
  border: 1.5px solid #52C5CD;
  border-radius: 4px;
  padding: 8px 22px;
  font-family: ${FONT};
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: #ffffff;
  cursor: pointer;

  &:hover { background: #3faab1; border-color: #3faab1; }
  &:disabled { background: #a7dde2; border-color: #a7dde2; cursor: default; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const SuccessBox = styled.div`
  padding: 8px 4px 4px;
  animation: ${fadeIn} 180ms ease-out;
`;

const SuccessTitle = styled.div`
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  margin-bottom: 6px;
`;

const SuccessBody = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.72);
  margin: 0 0 20px;
`;

class ContactSupport extends PureComponent {
  state = {
    open: false,
    subject: '',
    message: '',
    submitting: false,
    sent: false,
    fieldErrors: null, // { subject?: string, message?: string }
    formError: null,   // string
  };

  isMounted_ = false;

  componentDidMount() {
    this.isMounted_ = true;
    EventEmitter.getInstance().on(OPEN_CONTACT_SUPPORT_EVENT, this.handleOpen);
    document.addEventListener('keydown', this.handleKeydown);
  }

  componentWillUnmount() {
    this.isMounted_ = false;
    EventEmitter.getInstance().off(OPEN_CONTACT_SUPPORT_EVENT, this.handleOpen);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleOpen = () => {
    // Reset on every open so a prior success/error state doesn't
    // linger; keep subject/message empty by default (users rarely
    // want to resend the same message).
    this.setState({
      open: true,
      subject: '',
      message: '',
      submitting: false,
      sent: false,
      fieldErrors: null,
      formError: null,
    });
  };

  handleClose = () => {
    if (this.state.submitting) return; // don't close mid-flight
    this.setState({ open: false });
  };

  handleKeydown = (e) => {
    if (e.key === 'Escape' && this.state.open && !this.state.submitting) {
      this.setState({ open: false });
    }
  };

  handleSubjectChange = (e) => {
    const v = e.target.value.slice(0, SUBJECT_MAX);
    this.setState({ subject: v, fieldErrors: null });
  };

  handleMessageChange = (e) => {
    const v = e.target.value.slice(0, MESSAGE_MAX);
    this.setState({ message: v, fieldErrors: null });
  };

  buildPayload = () => {
    const { subject, message } = this.state;
    const url = (typeof window !== 'undefined' && window.location)
      ? window.location.href
      : '';
    // Auto-append current page URL so support can jump straight to
    // wherever the user was when they hit trouble. Two blank lines
    // between the message body and the appended context keeps it
    // visually separated in the email BE forwards.
    const messageWithContext = url
      ? `${message.trim()}\n\n---\nPage: ${url}`
      : message.trim();
    return {
      subject: subject.trim(),
      message: messageWithContext,
    };
  };

  handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const { subject, message, submitting } = this.state;
    if (submitting) return;
    // Client-side required-field validation. Server also validates,
    // but catching it here saves a round-trip on the obvious cases.
    const trimmedSubject = subject.trim();
    const trimmedMessage = message.trim();
    const fieldErrors = {};
    if (!trimmedSubject) fieldErrors.subject = 'Subject is required.';
    if (!trimmedMessage) fieldErrors.message = 'Message is required.';
    if (Object.keys(fieldErrors).length > 0) {
      this.setState({ fieldErrors, formError: null });
      return;
    }

    this.setState({ submitting: true, fieldErrors: null, formError: null });
    submitSupportMessage(this.buildPayload())
      .then(() => {
        if (!this.isMounted_) return;
        this.setState({ submitting: false, sent: true });
      })
      .catch((err) => {
        if (!this.isMounted_) return;
        this.handleError(err);
      });
  };

  handleError = (err) => {
    const status = err && err.status;
    const body = err && err.response && err.response.body;
    if (status === 400 && body && typeof body === 'object') {
      const fieldErrors = {};
      if (Array.isArray(body.subject) && body.subject.length > 0) {
        fieldErrors.subject = body.subject[0];
      }
      if (Array.isArray(body.message) && body.message.length > 0) {
        fieldErrors.message = body.message[0];
      }
      this.setState({
        submitting: false,
        fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : null,
        formError: Object.keys(fieldErrors).length > 0
          ? null
          : 'Please check the form and try again.',
      });
      return;
    }
    if (status === 429) {
      this.setState({
        submitting: false,
        formError:
          "You've sent a lot of messages recently. Please try again in about an hour.",
      });
      return;
    }
    if (status === 401) {
      // Fall through — App-level auth handling will pick up the 401
      // on the next request. Show a message so the user isn't left
      // wondering; the form contents are preserved by not clearing
      // state.
      this.setState({
        submitting: false,
        formError: 'Your session expired. Please log in again and retry.',
      });
      return;
    }
    this.setState({
      submitting: false,
      formError: 'Something went wrong. Please try again.',
    });
  };

  renderForm() {
    const { subject, message, submitting, fieldErrors, formError } = this.state;
    const messageWarn = message.length >= MESSAGE_COUNTER_WARN;
    return (
      <form onSubmit={this.handleSubmit} noValidate>
        <Subtitle>
          Send us a note and support will reach out by email.
        </Subtitle>
        <IntroNote>
          For screenshots or attachments, email support@marketgames.io directly.
        </IntroNote>
        {formError && <FormError>{formError}</FormError>}
        <FieldGroup>
          <Label htmlFor="contact-support-subject">Subject</Label>
          <Input
            id="contact-support-subject"
            type="text"
            value={subject}
            onChange={this.handleSubjectChange}
            disabled={submitting}
            maxLength={SUBJECT_MAX}
            autoFocus
          />
          {fieldErrors && fieldErrors.subject && (
            <FieldError>{fieldErrors.subject}</FieldError>
          )}
        </FieldGroup>
        <FieldGroup>
          <Label htmlFor="contact-support-message">Message</Label>
          <Textarea
            id="contact-support-message"
            value={message}
            onChange={this.handleMessageChange}
            disabled={submitting}
            maxLength={MESSAGE_MAX}
          />
          <CounterRow>
            <Counter warn={messageWarn}>
              {message.length} / {MESSAGE_MAX}
            </Counter>
          </CounterRow>
          {fieldErrors && fieldErrors.message && (
            <FieldError>{fieldErrors.message}</FieldError>
          )}
        </FieldGroup>
        <ButtonRow>
          <CancelButton type="button" onClick={this.handleClose} disabled={submitting}>
            Cancel
          </CancelButton>
          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send Message'}
          </SubmitButton>
        </ButtonRow>
      </form>
    );
  }

  renderSuccess() {
    return (
      <SuccessBox>
        <SuccessTitle>Message sent</SuccessTitle>
        <SuccessBody>
          Thanks for reaching out. Support will reach out to you by email soon.
        </SuccessBody>
        <ButtonRow>
          <SubmitButton type="button" onClick={this.handleClose}>
            Done
          </SubmitButton>
        </ButtonRow>
      </SuccessBox>
    );
  }

  render() {
    const { open, sent } = this.state;
    return (
      <div>
        <Fab
          type="button"
          aria-label="Contact support"
          title="Contact support"
          onClick={this.handleOpen}
        >
          ?
        </Fab>
        {open && (
          <Backdrop onClick={this.handleClose}>
            <Card onClick={(e) => e.stopPropagation()}>
              <HeaderRow>
                <TitleGroup>
                  <LogoIcon src={mgLogo} alt="" />
                  <Title>Contact Market Games Support</Title>
                </TitleGroup>
                <CloseButton
                  type="button"
                  aria-label="Close"
                  onClick={this.handleClose}
                >
                  ×
                </CloseButton>
              </HeaderRow>
              {sent ? this.renderSuccess() : this.renderForm()}
            </Card>
          </Backdrop>
        )}
      </div>
    );
  }
}

export default ContactSupport;



// WEBPACK FOOTER //
// ./src/components/contact-support/index.js